// Print Format Configuration Tab
// Sprint 3: Format & Options UI

import React from 'react';
import { Label } from '@/app/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/app/components/ui/radio-group';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Input } from '@/app/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Separator } from '@/app/components/ui/separator';
import { FileText, Table2 } from 'lucide-react';
import type { PrintFormatOptions, PrintPageSize } from '@/app/types/print';
import { PAGE_SIZE_INFO, VALIDATION } from '@/app/utils/printConstants';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface PrintFormatTabProps {
  format: PrintFormatOptions;
  onUpdateFormat: (updates: Partial<PrintFormatOptions>) => void;
  onUpdatePDFOptions: (updates: Partial<PrintFormatOptions['pdf']>) => void;
  onUpdateExcelOptions: (updates: Partial<PrintFormatOptions['excel']>) => void;
  onUpdatePageSize: (pageSize: PrintPageSize) => void;
}

export function PrintFormatTab({
  format,
  onUpdateFormat,
  onUpdatePDFOptions,
  onUpdateExcelOptions,
  onUpdatePageSize,
}: PrintFormatTabProps) {
  const { t } = useLanguage();
  const isPDF = format.format === 'pdf';
  const isExcel = format.format === 'excel-csv';
  
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h3 className="text-lg font-medium mb-2">{t.table.printDialog.format.title}</h3>
        <p className="text-sm text-muted-foreground">
          {t.table.printDialog.format.subtitle}
        </p>
      </div>
      
      {/* Format Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t.table.printDialog.format.exportFormat.title}</CardTitle>
          <CardDescription>{t.table.printDialog.format.exportFormat.desc}</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={format.format}
            onValueChange={(value) =>
              onUpdateFormat({ format: value as 'pdf' | 'excel-csv' })
            }
          >
            <div className="grid gap-3">
              <Card
                className={`cursor-pointer transition-colors ${
                  isPDF ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                }`}
                onClick={() => onUpdateFormat({ format: 'pdf' })}
              >
                <CardHeader className="p-4">
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="pdf" id="format-pdf" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-5 h-5" />
                        <CardTitle className="text-base">{t.table.printDialog.format.exportFormat.pdf.title}</CardTitle>
                      </div>
                      <CardDescription>
                        {t.table.printDialog.format.exportFormat.pdf.desc}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
              
              <Card
                className={`cursor-pointer transition-colors ${
                  isExcel ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                }`}
                onClick={() => onUpdateFormat({ format: 'excel-csv' })}
              >
                <CardHeader className="p-4">
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="excel-csv" id="format-excel" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Table2 className="w-5 h-5" />
                        <CardTitle className="text-base">{t.table.printDialog.format.exportFormat.excel.title}</CardTitle>
                      </div>
                      <CardDescription>
                        {t.table.printDialog.format.exportFormat.excel.desc}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>
      
      {/* PDF-specific Options */}
      {isPDF && format.pdf && (
        <>
          <Separator />
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t.table.printDialog.format.pageSetup.title}</CardTitle>
              <CardDescription>{t.table.printDialog.format.pageSetup.desc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pageSize">{t.table.printDialog.format.pageSetup.size}</Label>
                  <Select
                    value={format.pdf.pageSize}
                    onValueChange={(value) => onUpdatePageSize(value as PrintPageSize)}
                  >
                    <SelectTrigger id="pageSize">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PAGE_SIZE_INFO).map(([key, info]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex flex-col">
                            <span>{info.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {info.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="orientation">{t.table.printDialog.format.pageSetup.orientation}</Label>
                  <Select
                    value={format.pdf.orientation}
                    onValueChange={(value) =>
                      onUpdatePDFOptions({
                        orientation: value as 'portrait' | 'landscape',
                      })
                    }
                  >
                    <SelectTrigger id="orientation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portrait">{t.table.printDialog.format.pageSetup.portrait}</SelectItem>
                      <SelectItem value="landscape">{t.table.printDialog.format.pageSetup.landscape}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t.table.printDialog.format.margins.title}</CardTitle>
              <CardDescription>{t.table.printDialog.format.margins.desc}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="marginTop">{t.table.printDialog.format.margins.top}</Label>
                  <Input
                    id="marginTop"
                    type="number"
                    min={VALIDATION.MIN_MARGIN}
                    max={VALIDATION.MAX_MARGIN}
                    value={format.pdf.margins.top}
                    onChange={(e) =>
                      onUpdatePDFOptions({
                        margins: {
                          ...format.pdf!.margins,
                          top: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="marginBottom">{t.table.printDialog.format.margins.bottom}</Label>
                  <Input
                    id="marginBottom"
                    type="number"
                    min={VALIDATION.MIN_MARGIN}
                    max={VALIDATION.MAX_MARGIN}
                    value={format.pdf.margins.bottom}
                    onChange={(e) =>
                      onUpdatePDFOptions({
                        margins: {
                          ...format.pdf!.margins,
                          bottom: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="marginLeft">{t.table.printDialog.format.margins.left}</Label>
                  <Input
                    id="marginLeft"
                    type="number"
                    min={VALIDATION.MIN_MARGIN}
                    max={VALIDATION.MAX_MARGIN}
                    value={format.pdf.margins.left}
                    onChange={(e) =>
                      onUpdatePDFOptions({
                        margins: {
                          ...format.pdf!.margins,
                          left: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="marginRight">{t.table.printDialog.format.margins.right}</Label>
                  <Input
                    id="marginRight"
                    type="number"
                    min={VALIDATION.MIN_MARGIN}
                    max={VALIDATION.MAX_MARGIN}
                    value={format.pdf.margins.right}
                    onChange={(e) =>
                      onUpdatePDFOptions({
                        margins: {
                          ...format.pdf!.margins,
                          right: parseFloat(e.target.value),
                        },
                      })
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t.table.printDialog.format.typography.title}</CardTitle>
              <CardDescription>{t.table.printDialog.format.typography.desc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fontSize">{t.table.printDialog.format.typography.size}</Label>
                  <Input
                    id="fontSize"
                    type="number"
                    min={VALIDATION.MIN_FONT_SIZE}
                    max={VALIDATION.MAX_FONT_SIZE}
                    value={format.pdf.fontSize}
                    onChange={(e) =>
                      onUpdatePDFOptions({ fontSize: parseInt(e.target.value, 10) })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {t.table.printDialog.format.typography.range.replace('{min}', String(VALIDATION.MIN_FONT_SIZE)).replace('{max}', String(VALIDATION.MAX_FONT_SIZE))}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="fontFamily">{t.table.printDialog.format.typography.font}</Label>
                  <Select
                    value={format.pdf.fontFamily}
                    onValueChange={(value) => onUpdatePDFOptions({ fontFamily: value })}
                  >
                    <SelectTrigger id="fontFamily">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="helvetica">Helvetica</SelectItem>
                      <SelectItem value="times">Times</SelectItem>
                      <SelectItem value="courier">Courier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t.table.printDialog.format.display.title}</CardTitle>
              <CardDescription>{t.table.printDialog.format.display.desc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeGridLines"
                  checked={format.pdf.includeGridLines}
                  onCheckedChange={(checked) =>
                    onUpdatePDFOptions({ includeGridLines: Boolean(checked) })
                  }
                />
                <Label htmlFor="includeGridLines" className="cursor-pointer">
                  {t.table.printDialog.format.display.gridLines}
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeMetadataHeader"
                  checked={format.pdf.includeMetadataHeader}
                  onCheckedChange={(checked) =>
                    onUpdatePDFOptions({ includeMetadataHeader: Boolean(checked) })
                  }
                />
                <Label htmlFor="includeMetadataHeader" className="cursor-pointer">
                  {t.table.printDialog.format.display.metadataHeader}
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includePageNumbers"
                  checked={format.pdf.includePageNumbers}
                  onCheckedChange={(checked) =>
                    onUpdatePDFOptions({ includePageNumbers: Boolean(checked) })
                  }
                />
                <Label htmlFor="includePageNumbers" className="cursor-pointer">
                  {t.table.printDialog.format.display.pageNumbers}
                </Label>
              </div>
              
              {format.pdf.includePageNumbers && (
                <div className="ml-6 space-y-2">
                  <Label htmlFor="pageNumberPosition">{t.table.printDialog.format.display.pagePos}</Label>
                  <Select
                    value={format.pdf.pageNumberPosition}
                    onValueChange={(value) =>
                      onUpdatePDFOptions({
                        pageNumberPosition: value as typeof format.pdf.pageNumberPosition,
                      })
                    }
                  >
                    <SelectTrigger id="pageNumberPosition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bottom-left">{t.table.printDialog.format.display.posLeft}</SelectItem>
                      <SelectItem value="bottom-center">{t.table.printDialog.format.display.posCenter}</SelectItem>
                      <SelectItem value="bottom-right">{t.table.printDialog.format.display.posRight}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <Separator className="my-4" />
              
              {/* Custom Fields Display */}
              <div className="space-y-2">
                <Label htmlFor="customFieldsDisplay">{t.table.printDialog.format.display.customFields}</Label>
                <Select
                  value={format.pdf.customFieldsDisplay}
                  onValueChange={(value) =>
                    onUpdatePDFOptions({
                      customFieldsDisplay: value as 'none' | 'above-table' | 'below-table',
                    })
                  }
                >
                  <SelectTrigger id="customFieldsDisplay">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {t.table.printDialog.format.display.cfNone}
                    </SelectItem>
                    <SelectItem value="above-table">{t.table.printDialog.format.display.cfAbove}</SelectItem>
                    <SelectItem value="below-table">{t.table.printDialog.format.display.cfBelow}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t.table.printDialog.format.display.cfNote}
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}
      
      {/* Excel/CSV-specific Options */}
      {isExcel && format.excel && (
        <>
          <Separator />
          
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t.table.printDialog.format.csv.title}</CardTitle>
              <CardDescription>{t.table.printDialog.format.csv.desc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="separator">{t.table.printDialog.format.csv.separator}</Label>
                <Select
                  value={format.excel.separator}
                  onValueChange={(value) =>
                    onUpdateExcelOptions({ separator: value as ',' | ';' | '\t' })
                  }
                >
                  <SelectTrigger id="separator">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=",">{t.table.printDialog.format.csv.comma}</SelectItem>
                    <SelectItem value=";">{t.table.printDialog.format.csv.semicolon}</SelectItem>
                    <SelectItem value="\t">{t.table.printDialog.format.csv.tab}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeFormulas"
                  checked={format.excel.includeFormulas}
                  onCheckedChange={(checked) =>
                    onUpdateExcelOptions({ includeFormulas: Boolean(checked) })
                  }
                  disabled
                />
                <Label
                  htmlFor="includeFormulas"
                  className="cursor-not-allowed text-muted-foreground"
                >
                  {t.table.printDialog.format.csv.formulas} {t.table.printDialog.format.csv.notSupported}
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeStyles"
                  checked={format.excel.includeStyles}
                  onCheckedChange={(checked) =>
                    onUpdateExcelOptions({ includeStyles: Boolean(checked) })
                  }
                  disabled
                />
                <Label
                  htmlFor="includeStyles"
                  className="cursor-not-allowed text-muted-foreground"
                >
                  {t.table.printDialog.format.csv.styles} {t.table.printDialog.format.csv.notSupported}
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeProjectMetadata"
                  checked={format.excel.includeProjectMetadata}
                  onCheckedChange={(checked) =>
                    onUpdateExcelOptions({ includeProjectMetadata: Boolean(checked) })
                  }
                />
                <Label htmlFor="includeProjectMetadata" className="cursor-pointer">
                  {t.table.printDialog.format.csv.projectMetadata}
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                {t.table.printDialog.format.csv.metadataNote}
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}